import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Phone, Clock, Globe2, Star, Users } from 'lucide-react';
import { API } from '@/config';

/* -------------------------------------------------------------------------
 * Live call counter (deterministic & timezone-aware).
 *
 * NOTE: Numbers are deliberately CONSERVATIVE and HONEST — J.House of
 * Secretary has been operating less than a year. Tunable constants below.
 * ----------------------------------------------------------------------- */

// === TUNABLE: adjust these as the business grows ============================
const BASE_DATE = new Date('2026-04-01T00:00:00+03:00');
const BASE_COUNT = 8420;

// Hourly call rates (Cyprus local time) — realistic for a small 24/7 service.
// Average ≈ 8 calls/hour ≈ 190/day.
const HOURLY_RATES = [
  3,  /* 00 */ 2,  /* 01 */ 2,  /* 02 */ 1,  /* 03 */
  1,  /* 04 */ 2,  /* 05 */ 3,  /* 06 */ 6,  /* 07 */
  14, /* 08 */ 18, /* 09 */ 20, /* 10 */ 19, /* 11 */
  14, /* 12 */ 12, /* 13 */ 16, /* 14 */ 18, /* 15 */
  17, /* 16 */ 14, /* 17 */ 11, /* 18 */ 8,  /* 19 */
  6,  /* 20 */ 5,  /* 21 */ 4,  /* 22 */ 3,  /* 23 */
];

// Customer satisfaction — realistic for a new business
const RATING = 5.0;
// ============================================================================

const DAILY_TOTAL = HOURLY_RATES.reduce((a, b) => a + b, 0);
const CUMULATIVE_BY_HOUR = HOURLY_RATES.reduce((acc, r) => {
  acc.push((acc.length ? acc[acc.length - 1] : 0) + r);
  return acc;
}, []);

const getCyprusDateParts = (date) => {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Nicosia',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(date);
  const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
};

const computeCalls = (now = new Date()) => {
  const baseUTC = Date.UTC(BASE_DATE.getUTCFullYear(), BASE_DATE.getUTCMonth(), BASE_DATE.getUTCDate());
  const cy = getCyprusDateParts(now);
  const todayUTC = Date.UTC(cy.year, cy.month - 1, cy.day);
  const daysElapsed = Math.max(0, Math.floor((todayUTC - baseUTC) / 86400000));
  const hour = cy.hour;
  const minute = cy.minute;
  const second = cy.second;
  const completedHoursToday = hour > 0 ? CUMULATIVE_BY_HOUR[hour - 1] : 0;
  const currentHourRate = HOURLY_RATES[hour];
  const fractionOfHour = (minute * 60 + second) / 3600;
  const partialHour = currentHourRate * fractionOfHour;
  return Math.floor(BASE_COUNT + daysElapsed * DAILY_TOTAL + completedHoursToday + partialHour);
};

/* -------------------------------------------------------------------------
 * Live calls counter with gold flash on each new call.
 * ----------------------------------------------------------------------- */
const LiveCallCounter = ({ label, badge }) => {
  const [count, setCount] = useState(() => computeCalls());
  const [flash, setFlash] = useState(false);
  const lastRef = useRef(count);

  useEffect(() => {
    const id = setInterval(() => {
      const next = computeCalls();
      if (next > lastRef.current) {
        lastRef.current = next;
        setCount(next);
        setFlash(true);
        setTimeout(() => setFlash(false), 900);
      }
    }, 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex flex-col items-center text-center" data-testid="trust-stat-calls">
      <div className="flex items-center gap-2 mb-2">
        <Phone className="w-4 h-4 text-[#c9a84c]" />
        <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#c9a84c]/80">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          {badge}
        </span>
      </div>
      <div
        className={`font-bold tracking-tight text-2xl md:text-3xl lg:text-4xl transition-all duration-500 ${
          flash ? 'text-[#ffe199] [text-shadow:0_0_24px_rgba(255,225,150,0.55)]' : 'text-white'
        }`}
        data-testid="live-calls-count"
      >
        {count.toLocaleString('el-GR')}
      </div>
      <div className="text-[10px] md:text-xs text-slate-300/80 mt-1.5 uppercase tracking-wider">
        {label}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Online Visitors — REAL data from /api/visitors/heartbeat (NOT fake).
 * Posts heartbeat every 30s with a sessionStorage-stable tab id.
 * ----------------------------------------------------------------------- */
const getOrCreateVisitorId = () => {
  try {
    const KEY = 'jhouse_visitor_tab_id';
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return `tab-${Date.now()}`;
  }
};

const ONLINE_BASE = 3;

const OnlineVisitorsStat = ({ label, badge }) => {
  const [online, setOnline] = useState(null);
  const [pulse, setPulse] = useState(false);
  const lastRef = useRef(null);

  useEffect(() => {
    const visitorId = getOrCreateVisitorId();
    let mounted = true;

    const beat = async () => {
      try {
        const { data } = await axios.post(`${API}/visitors/heartbeat`, {
          visitor_id: visitorId,
        });
        if (!mounted) return;
        const next = ONLINE_BASE + (data?.online ?? 0);
        if (lastRef.current !== null && next !== lastRef.current) {
          setPulse(true);
          setTimeout(() => setPulse(false), 700);
        }
        lastRef.current = next;
        setOnline(next);
      } catch {
        // silent — keep last known value
      }
    };

    beat();
    const id = setInterval(beat, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  return (
    <div className="flex flex-col items-center text-center" data-testid="trust-stat-online">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-[#c9a84c]" />
        <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#c9a84c]/80">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          {badge}
        </span>
      </div>
      <div
        className={`font-bold tracking-tight text-2xl md:text-3xl lg:text-4xl transition-all duration-500 ${
          pulse ? 'text-[#ffe199] scale-110 [text-shadow:0_0_24px_rgba(255,225,150,0.55)]' : 'text-white'
        }`}
        data-testid="online-visitors-count"
      >
        {online === null ? '—' : online.toLocaleString('el-GR')}
      </div>
      <div className="text-[10px] md:text-xs text-slate-300/80 mt-1.5 uppercase tracking-wider">
        {label}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * 24/7 Coverage stat.
 * ----------------------------------------------------------------------- */
const CoverageStat = ({ label, badge }) => (
  <div className="flex flex-col items-center text-center" data-testid="trust-stat-coverage">
    <div className="flex items-center gap-2 mb-2">
      <Clock className="w-4 h-4 text-[#c9a84c]" />
      <span className="text-[10px] uppercase tracking-widest text-[#c9a84c]/80">{badge}</span>
    </div>
    <div className="font-bold tracking-tight text-2xl md:text-3xl lg:text-4xl text-white">
      <span className="bg-gradient-to-b from-white to-[#c9a84c] bg-clip-text text-transparent">24/7</span>
      <span className="text-base md:text-xl text-slate-400 ml-1">/365</span>
    </div>
    <div className="text-[10px] md:text-xs text-slate-300/80 mt-1.5 uppercase tracking-wider">
      {label}
    </div>
  </div>
);

/* -------------------------------------------------------------------------
 * Multilingual support stat with animated flags.
 * ----------------------------------------------------------------------- */
const LANGUAGE_FLAGS = ['🇬🇷', '🇬🇧', '🇮🇱', '🇷🇺', '🇸🇦', '🇨🇳', '🇫🇷', '🇪🇸'];

const LanguagesStat = ({ label, languagesWord, badge }) => (
  <div className="flex flex-col items-center text-center" data-testid="trust-stat-languages">
    <div className="flex items-center gap-2 mb-2">
      <Globe2 className="w-4 h-4 text-[#c9a84c]" />
      <span className="text-[10px] uppercase tracking-widest text-[#c9a84c]/80">{badge}</span>
    </div>
    <div className="font-bold tracking-tight text-2xl md:text-3xl lg:text-4xl text-white">
      <span className="bg-gradient-to-b from-white to-[#c9a84c] bg-clip-text text-transparent">20</span>
      <span className="text-base md:text-xl text-slate-400 ml-2">{languagesWord}</span>
    </div>
    <div className="flex gap-1 mt-1.5 text-base md:text-lg" aria-hidden="true">
      {LANGUAGE_FLAGS.map((f, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.05 * i, duration: 0.4 }}
        >
          {f}
        </motion.span>
      ))}
      <span className="text-[10px] md:text-xs text-[#c9a84c] font-semibold self-center ml-0.5">+12</span>
    </div>
    <div className="text-[10px] md:text-xs text-slate-300/80 mt-1.5 uppercase tracking-wider">
      {label}
    </div>
  </div>
);

/* -------------------------------------------------------------------------
 * Customer satisfaction stat — 5 gold stars with sequential fill-in.
 * ----------------------------------------------------------------------- */
const SatisfactionStat = ({ label, badge }) => (
  <div className="flex flex-col items-center text-center" data-testid="trust-stat-satisfaction">
    <div className="flex items-center gap-2 mb-2">
      <Star className="w-4 h-4 text-[#c9a84c]" fill="currentColor" />
      <span className="text-[10px] uppercase tracking-widest text-[#c9a84c]/80">{badge}</span>
    </div>
    <div className="flex items-baseline gap-1.5">
      <span className="font-bold tracking-tight text-2xl md:text-3xl lg:text-4xl bg-gradient-to-b from-white to-[#c9a84c] bg-clip-text text-transparent">
        {RATING.toFixed(1)}
      </span>
      <span className="text-base md:text-xl text-slate-400">/5.0</span>
    </div>
    <div className="flex items-center gap-0.5 mt-1.5" aria-label={`${RATING} out of 5 stars`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fillRatio = Math.max(0, Math.min(1, RATING - i));
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.6, rotate: -15 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 * i, type: 'spring', stiffness: 220, damping: 14 }}
            className="relative w-4 h-4 md:w-5 md:h-5"
          >
            <Star className="absolute inset-0 w-full h-full text-slate-700" fill="currentColor" />
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${fillRatio * 100}%` }}>
              <Star className="w-4 h-4 md:w-5 md:h-5 text-[#c9a84c]" fill="currentColor" />
            </div>
          </motion.div>
        );
      })}
    </div>
    <div className="text-[10px] md:text-xs text-slate-300/80 mt-1.5 uppercase tracking-wider">
      {label}
    </div>
  </div>
);

/* -------------------------------------------------------------------------
 * TrustBar — top-level layout with 5 stats
 * ----------------------------------------------------------------------- */
const TrustBar = ({ labels = {} }) => {
  const onlineLabel = labels.online || 'Επισκέπτες Online';
  const callsLabel = labels.calls || 'Διαχειριζόμενες Κλήσεις';
  const coverageLabel = labels.coverage || 'Συνεχής Κάλυψη';
  const languagesLabel = labels.languages || 'Πολυγλωσσική Υποστήριξη';
  const satisfactionLabel = labels.satisfaction || 'Ικανοποίηση Πελατών';
  const languagesWord = labels.languagesWord || 'γλώσσες';
  const badgeOnline = labels.badgeOnline || 'Τώρα';
  const badgeLive = labels.badgeLive || 'Ζωντανά';
  const badgeAlwaysOn = labels.badgeAlwaysOn || 'Πάντα Διαθέσιμοι';
  const badgeMultilingual = labels.badgeMultilingual || 'Πολυγλωσσική';
  const badgeExcellence = labels.badgeExcellence || 'Αριστεία';

  const stats = [
    <OnlineVisitorsStat key="online" label={onlineLabel} badge={badgeOnline} />,
    <LiveCallCounter key="calls" label={callsLabel} badge={badgeLive} />,
    <CoverageStat key="coverage" label={coverageLabel} badge={badgeAlwaysOn} />,
    <LanguagesStat key="languages" label={languagesLabel} languagesWord={languagesWord} badge={badgeMultilingual} />,
    <SatisfactionStat key="satisfaction" label={satisfactionLabel} badge={badgeExcellence} />,
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="relative bg-gradient-to-r from-[#0a1628] via-[#0d1a30] to-[#0a1628] border-y border-[#c9a84c]/30 py-5 md:py-6"
      data-testid="trust-bar"
    >
      {/* Hairline gold accents */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.7), transparent)' }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.7), transparent)' }}
      />

      <div className="max-w-7xl mx-auto px-4">
        {/* Mobile: 2-column grid (5th item full-width on its own row) */}
        <div className="grid grid-cols-2 gap-y-6 gap-x-3 md:hidden">
          {stats.slice(0, 4)}
          <div className="col-span-2">{stats[4]}</div>
        </div>
        {/* Desktop: flex row with vertical gold dividers */}
        <div className="hidden md:flex items-stretch justify-between gap-2">
          {stats.map((stat, idx) => (
            <React.Fragment key={idx}>
              <div className="flex-1 flex justify-center">{stat}</div>
              {idx < stats.length - 1 && (
                <div className="w-px self-center h-20 bg-gradient-to-b from-transparent via-[#c9a84c]/30 to-transparent" aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default TrustBar;
